import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { ARCHETYPE_COLORS, CAREER_STAGE_LABELS, Profile } from '../types';

interface Props {
  institutionId?: string | null;
}

function pct(num: number, total: number): number {
  return total === 0 ? 0 : Math.round((num / total) * 100);
}

function BarRow({ label, count, total, color }: { label: string; count: number; total: number; color: string }) {
  const p = pct(count, total);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', width: 130, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
      <div style={{ flex: 1, height: 7, background: 'var(--border)', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${p}%`, background: color, borderRadius: 10, transition: 'width 0.4s' }} />
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', width: 28, textAlign: 'right' }}>{count}</div>
      <div style={{ fontSize: 11, color: 'var(--text-3)', width: 34, textAlign: 'right' }}>{p}%</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="panel" style={{ marginBottom: 18 }}>
      <div className="panel-header"><div className="panel-title">{title}</div></div>
      <div style={{ padding: '16px 18px' }}>{children}</div>
    </div>
  );
}

export default function InsightsPage({ institutionId }: Props) {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = supabase
      .from('profiles')
      .select('sport,career_stage,athlete_archetype,assessment_status,subscription_tier,updated_at')
      .eq('is_admin', false)
      .eq('user_type', 'athlete');
    if (institutionId) q = q.eq('institution_id', institutionId);
    q.then(({ data }) => {
      setProfiles((data as Profile[]) ?? []);
      setLoading(false);
    });
  }, [institutionId]);

  const isScoped = !!institutionId;
  const total = profiles.length;

  // Assessment funnel
  const notStarted = profiles.filter(p => !p.assessment_status || p.assessment_status === 'not_started').length;
  const inProgress = profiles.filter(p => p.assessment_status === 'in_progress').length;
  const completed = profiles.filter(p => p.assessment_status === 'completed').length;

  // Archetype distribution
  const archetypeCounts: Record<string, number> = {};
  profiles.forEach(p => { if (p.athlete_archetype) { const arch = p.athlete_archetype.replace(/^The\s+/i, ''); archetypeCounts[arch] = (archetypeCounts[arch] || 0) + 1; } });

  // Career stage breakdown
  const stageCounts: Record<string, number> = {};
  profiles.forEach(p => { if (p.career_stage) stageCounts[p.career_stage] = (stageCounts[p.career_stage] || 0) + 1; });

  // Sport breakdown
  const sportCounts: Record<string, number> = {};
  profiles.forEach(p => {
    if (p.sport) {
      const sport = p.sport.split('·')[0].trim();
      sportCounts[sport] = (sportCounts[sport] || 0) + 1;
    }
  });
  const topSports = Object.entries(sportCounts).sort((a, b) => b[1] - a[1]).slice(0, 8);

  // Active users: profiles updated in last 7 days
  const sevenDaysAgo = Date.now() - 7 * 86400000;
  const activeThisWeek = profiles.filter(p => p.updated_at && new Date(p.updated_at).getTime() > sevenDaysAgo).length;

  // Archetype + sport cross analysis
  const archetypePerSport: Record<string, Record<string, number>> = {};
  profiles.forEach(p => {
    if (p.sport && p.athlete_archetype) {
      const sport = p.sport.split('·')[0].trim();
      if (!archetypePerSport[sport]) archetypePerSport[sport] = {};
      archetypePerSport[sport][p.athlete_archetype] = (archetypePerSport[sport][p.athlete_archetype] || 0) + 1;
    }
  });

  if (loading) {
    return (
      <div>
        <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 28, color: 'var(--text)', marginBottom: 8 }}>Insights</div>
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>Loading analytics...</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 22 }}>
        <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 28, color: 'var(--text)' }}>Insights</div>
        <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 4 }}>
          {isScoped ? `Your institution analytics — ${total} athlete${total !== 1 ? 's' : ''}` : `Platform analytics — ${total} athletes`}
        </div>
      </div>

      {/* Summary tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
        {[
          { label: 'Total Athletes', value: total, color: '#014c8d' },
          { label: 'Assessments Complete', value: completed, color: '#0fa86d' },
          { label: 'Active This Week', value: activeThisWeek, color: '#e88c0e' },
          { label: 'Completion Rate', value: `${pct(completed, total)}%`, color: '#bf1e2e' },
        ].map(s => (
          <div key={s.label} className="panel" style={{ padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: s.color }} />
            <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 32, color: 'var(--text)', lineHeight: 1 }}>{s.value}</div>
            <div className="label-caps" style={{ marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>

        {/* Assessment Funnel */}
        <Section title="Assessment Funnel">
          {total === 0 ? (
            <div style={{ color: 'var(--text-3)', textAlign: 'center', padding: '16px 0' }}>No data yet</div>
          ) : (
            <>
              <BarRow label="Total Athletes" count={total}       total={total} color="#014c8d" />
              <BarRow label="Started"        count={total - notStarted} total={total} color="#e88c0e" />
              <BarRow label="Completed"      count={completed}   total={total} color="#0fa86d" />
              {inProgress > 0 && <BarRow label="In Progress" count={inProgress} total={total} color="#6b46c1" />}
            </>
          )}
        </Section>

        {/* Archetype Distribution */}
        <Section title="Archetype Distribution">
          {Object.keys(archetypeCounts).length === 0 ? (
            <div style={{ color: 'var(--text-3)', textAlign: 'center', padding: '16px 0' }}>No archetypes assigned yet</div>
          ) : (
            ['Commander', 'Architect', 'Catalyst', 'Guardian'].map(arch => {
              if (!archetypeCounts[arch]) return null;
              return <BarRow key={arch} label={arch} count={archetypeCounts[arch]} total={completed || 1} color={ARCHETYPE_COLORS[arch]} />;
            })
          )}
        </Section>

      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>

        {/* Career Stage */}
        <Section title="Career Stage Breakdown">
          {Object.keys(stageCounts).length === 0 ? (
            <div style={{ color: 'var(--text-3)', textAlign: 'center', padding: '16px 0' }}>No career stage data</div>
          ) : (
            Object.entries(stageCounts).sort((a, b) => b[1] - a[1]).map(([stage, count], i) => {
              const stageColors = ['#014c8d', '#bf1e2e', '#0fa86d', '#e88c0e'];
              return <BarRow key={stage} label={CAREER_STAGE_LABELS[stage] || stage} count={count} total={total} color={stageColors[i % stageColors.length]} />;
            })
          )}
        </Section>

      </div>

      {/* Top Sports */}
      {topSports.length > 0 && (
        <Section title="Top Sports">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px' }}>
            {topSports.map(([sport, count], i) => {
              const colors = ['#014c8d', '#bf1e2e', '#0fa86d', '#e88c0e', '#6b46c1', '#0fa86d', '#014c8d', '#bf1e2e'];
              return <BarRow key={sport} label={sport} count={count} total={total} color={colors[i % colors.length]} />;
            })}
          </div>
        </Section>
      )}

      {/* Archetype × Sport cross-analysis */}
      {Object.keys(archetypePerSport).length > 0 && (
        <Section title="Archetype by Sport">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            {Object.entries(archetypePerSport).map(([sport, archs]) => {
              const topArch = Object.entries(archs).sort((a, b) => b[1] - a[1])[0];
              const color = topArch ? (ARCHETYPE_COLORS[topArch[0]] || '#4a6080') : '#4a6080';
              return (
                <div key={sport} style={{ background: 'var(--surface-2)', borderRadius: 9, padding: '12px 14px', border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600, marginBottom: 6 }}>{sport}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {Object.entries(archs).sort((a, b) => b[1] - a[1]).map(([arch, cnt]) => (
                      <div key={arch} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: ARCHETYPE_COLORS[arch] || '#4a6080', fontWeight: 600 }}>{arch}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>{cnt}</span>
                      </div>
                    ))}
                  </div>
                  {topArch && (
                    <div style={{ marginTop: 8, padding: '4px 8px', borderRadius: 8, background: color + '18', border: `1px solid ${color}33`, fontSize: 10, color, fontWeight: 700, textAlign: 'center' }}>
                      Dominant: {topArch[0]}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}
    </div>
  );
}
